import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useAppDispatch } from '../../store/hooks';
import { verifyMfa } from '../../store/authSlice';
import axios from '../../utils/axios';
import { IMfaChallenge } from '../../utils/auth.types';

const RESEND_SECONDS = 30;

const flushTextButtonSx = { px: 0, minWidth: 0, justifyContent: 'flex-start' };

const label = (channel: string, challenge: IMfaChallenge) =>
  channel === 'phone' ? challenge.maskedPhone : challenge.maskedEmail;

/**
 * Second step of sign-in when an admin has turned MFA on for the member.
 * Nothing reaches the application until the code checks out.
 */
export default function MfaChallengeForm({ challenge }: { challenge: IMfaChallenge }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentOn, setSentOn] = useState<string[]>(challenge.sentOn);
  const others = challenge.channels.filter(c => !sentOn.includes(c));
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = async (channel?: string) => {
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post('/api/v1/mfa/send', { mfaToken: challenge.mfaToken, channel });
      setSentOn(data.channels);
      setCooldown(RESEND_SECONDS);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not send the code.');
    }
    setBusy(false);
  };

  const submitOtp = async () => {
    setError('');
    setBusy(true);
    const result: any = await dispatch(verifyMfa({ mfaToken: challenge.mfaToken, otp }));
    if (result.type === 'auth/verifyMfa/fulfilled') {
      navigate('/home', { replace: true });
      return;
    }
    setError(result.payload?.message || 'Invalid OTP.');
    setBusy(false);
  };

  if (!challenge.channels.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Verification needed</Typography>
        <Typography variant="body2" color="error">{challenge.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Verify it&apos;s you</Typography>

      <Typography variant="body2" color="text.secondary">
        Code sent to {sentOn.map(c => label(c, challenge)).join(' and ')}. It expires in 10 minutes.
      </Typography>
      <TextField
        label="Verification code"
        value={otp}
        onChange={e => setOtp(e.target.value)}
        size="small"
        fullWidth
        autoFocus
      />
      <Button variant="contained" disabled={!otp || busy} onClick={submitOtp}>
        Verify &amp; continue
      </Button>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="text"
          size="small"
          disabled={busy || cooldown > 0}
          onClick={() => send(sentOn[0])}
          sx={{ ...flushTextButtonSx, color: '#d32f2f', '&.Mui-disabled': { color: '#d32f2f' } }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend'}
        </Button>
        {others.map(channel => (
          <Button key={channel} variant="text" size="small" disabled={busy || cooldown > 0} onClick={() => send(channel)} sx={flushTextButtonSx}>
            Send to {label(channel, challenge)} instead
          </Button>
        ))}
      </Box>

      {error && <Typography variant="body2" color="error">{error}</Typography>}
    </Box>
  );
}
